import type { RadioGroupResult } from '@myosh/odin-components'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { HierarchyCriteria } from '../../API/rtk-query-api/store/tmsApi'
import { defaultDateFieldOption } from '../../components/layout/training-matrix/display-options'
import type { DropdownOption } from '../../components/UI/multi-select-dropdown/MultiSelectDropdown'
import { resolveExportFormatValue } from '../../utils/export-format-util'
import type { DateFieldOption, ExportFormatType } from '../types/data-options'
import type { ExtendedDropdownResult } from '../types/hierarchy-values'

export interface TMViewFilters {
  competencies: Array<DropdownOption>
  people: Array<DropdownOption>
  managers: Array<DropdownOption>
  occupations: Array<DropdownOption>
  hierarchies: Array<HierarchyCriteria>
  hierarchyViewMode: RadioGroupResult
  applyHierarchyFiltersToOccupationRequirements: boolean
  showOtherCompetencies: boolean
  showDesiredCompetencies: boolean
  showAllReports: boolean
  showDates: boolean
  exportFormat: ExportFormatType
  selectedDisplayDate: DateFieldOption
  showGridSeparator: boolean
  gridlinesFrequency: number
  showManagersColumn: boolean
  showGroups: boolean
  expirationDateLimit: number
  slantHeaders: boolean
  showOverallCompliancePercentage: boolean
  showPercentageOfComplianceForCompetencies: boolean
  showPercentageOfComplianceForIndividuals: boolean
  showPercentageOfComplianceForGroups: boolean
  isColourBlindThemeEnabled: boolean
  columnsToShow: Array<ExtendedDropdownResult>
}

interface TrainingMatrixFilterState {
  showDates: boolean
  exportFormat: ExportFormatType
  slantHeaders: boolean
  selectedDisplayDate: DateFieldOption
  selectedHierarchies: Array<HierarchyCriteria>
  showGridSeparator: boolean
  gridlinesFrequency: number
  expirationDateLimit: number
  selectedCompetencies: Array<DropdownOption>
  selectedManagers: Array<DropdownOption>
  showAllReports: boolean
  showDesiredCompetencies: boolean
  showOtherCompetencies: boolean
  hierarchyViewMode: RadioGroupResult
  applyHierarchyFiltersToOccupationRequirements: boolean
  selectedOccupations: Array<DropdownOption>
  selectedPersons: Array<DropdownOption>
  columnsToShow: Array<ExtendedDropdownResult>
  showManagersColumn: boolean
  showOverallCompliancePercentage: boolean
  showPercentageOfComplianceForIndividuals: boolean
  showPercentageOfComplianceForCompetencies: boolean
  showPercentageOfComplianceForGroups: boolean
  showGroups: boolean
  isColourBlindThemeEnabled: boolean
}

const initialState: TrainingMatrixFilterState = {
  // Display tab filters
  showDates: false,
  exportFormat: 'ICONS',
  slantHeaders: true,
  selectedDisplayDate: defaultDateFieldOption,
  selectedHierarchies: [],
  showGridSeparator: true,
  gridlinesFrequency: 5,
  expirationDateLimit: 30,

  // Filter tab filters
  selectedCompetencies: [],
  selectedManagers: [],
  showAllReports: false,
  showDesiredCompetencies: false,
  showOtherCompetencies: false,

  // Hierachy tab filters
  hierarchyViewMode: { text: 'All', value: 'all' },
  applyHierarchyFiltersToOccupationRequirements: false,

  selectedOccupations: [],
  selectedPersons: [],

  // Control which columns to show
  columnsToShow: [],
  showManagersColumn: false,

  // Compliance UI triggers
  showOverallCompliancePercentage: false,
  showPercentageOfComplianceForIndividuals: false,
  showPercentageOfComplianceForCompetencies: false,
  showPercentageOfComplianceForGroups: false,
  showGroups: false,
  isColourBlindThemeEnabled: false
}

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setSelectedCompetences(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedCompetencies = action.payload
    },
    setSelectedManagers(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedManagers = action.payload
    },
    setShowAllReports(state, action: PayloadAction<boolean>) {
      state.showAllReports = action.payload
    },
    setShowOtherCompetencies(state, action: PayloadAction<boolean>) {
      state.showOtherCompetencies = action.payload
    },
    setShowDesirecCompetencies(state, action: PayloadAction<boolean>) {
      state.showDesiredCompetencies = action.payload
    },
    setSelectedPersons(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedPersons = action.payload
    },
    setSelectedOccupations(state, action: PayloadAction<Array<DropdownOption>>) {
      state.selectedOccupations = action.payload
    },
    setSelectedDisplayViewMode(state, action: PayloadAction<boolean>) {
      state.showDates = action.payload
      state.exportFormat = resolveExportFormatValue(action.payload, state.selectedDisplayDate)
    },
    setSelectedDisplayDate(state, action: PayloadAction<DateFieldOption>) {
      state.selectedDisplayDate = action.payload
      state.exportFormat = resolveExportFormatValue(state.showDates, action.payload)
    },
    setSlantHeaders(state, action: PayloadAction<boolean>) {
      state.slantHeaders = action.payload
    },
    setShowGridSeparator(state, action: PayloadAction<boolean>) {
      state.showGridSeparator = action.payload
    },
    setGridlinesFrequency(state, action: PayloadAction<number>) {
      state.gridlinesFrequency = action.payload
    },
    setExpirationDateLimit(state, action: PayloadAction<number>) {
      state.expirationDateLimit = action.payload
    },
    setSelectedHierarchyViewMode(state, action: PayloadAction<RadioGroupResult>) {
      state.hierarchyViewMode = action.payload
    },
    setSelectedHierarchies(state, action: PayloadAction<Array<HierarchyCriteria>>) {
      state.selectedHierarchies = action.payload
    },
    setApplyHierarchyFiltersToOccupationRequirements(state, action: PayloadAction<boolean>) {
      state.applyHierarchyFiltersToOccupationRequirements = action.payload
    },
    setColumnsToShow(state, action: PayloadAction<Array<ExtendedDropdownResult>>) {
      state.columnsToShow = action.payload
    },
    setShowManagersColumn(state, action: PayloadAction<boolean>) {
      state.showManagersColumn = action.payload
    },
    setShowOverallCompliancePercentage(state, action: PayloadAction<boolean>) {
      state.showOverallCompliancePercentage = action.payload
    },
    setShowPercentageOfComplianceForIndividuals(state, action: PayloadAction<boolean>) {
      state.showPercentageOfComplianceForIndividuals = action.payload
    },
    setShowPercentageOfComplianceForCompetencies(state, action: PayloadAction<boolean>) {
      state.showPercentageOfComplianceForCompetencies = action.payload
    },
    setShowPercentageOfComplianceForGroups(state, action: PayloadAction<boolean>) {
      state.showPercentageOfComplianceForGroups = action.payload
    },
    setShowGroups(state, action: PayloadAction<boolean>) {
      state.showGroups = action.payload
    },
    setIsColourBlindThemeEnabled(state, action: PayloadAction<boolean>) {
      state.isColourBlindThemeEnabled = action.payload
    },
    setViewFilters(state, action: PayloadAction<TMViewFilters>) {
      state.selectedCompetencies = action.payload.competencies
      state.selectedPersons = action.payload.people
      state.selectedManagers = action.payload.managers
      state.selectedOccupations = action.payload.occupations
      state.selectedHierarchies = action.payload.hierarchies
      state.hierarchyViewMode = action.payload.hierarchyViewMode
      state.applyHierarchyFiltersToOccupationRequirements = action.payload.applyHierarchyFiltersToOccupationRequirements
      state.showOtherCompetencies = action.payload.showOtherCompetencies
      state.showDesiredCompetencies = action.payload.showDesiredCompetencies
      state.showAllReports = action.payload.showAllReports
      state.showDates = action.payload.showDates
      state.exportFormat = action.payload.exportFormat
      state.selectedDisplayDate = action.payload.selectedDisplayDate
      state.showGridSeparator = action.payload.showGridSeparator
      state.gridlinesFrequency = action.payload.gridlinesFrequency
      state.showManagersColumn = action.payload.showManagersColumn
      state.showGroups = action.payload.showGroups
      state.expirationDateLimit = action.payload.expirationDateLimit
      state.slantHeaders = action.payload.slantHeaders
      state.showOverallCompliancePercentage = action.payload.showOverallCompliancePercentage
      state.showPercentageOfComplianceForCompetencies = action.payload.showPercentageOfComplianceForCompetencies
      state.showPercentageOfComplianceForIndividuals = action.payload.showPercentageOfComplianceForIndividuals
      state.showPercentageOfComplianceForGroups = action.payload.showPercentageOfComplianceForGroups
      state.isColourBlindThemeEnabled = action.payload.isColourBlindThemeEnabled
      state.columnsToShow = action.payload.columnsToShow
    }
  }
})

export const filtersReducer = filtersSlice.reducer
export const filtersActions = {
  ...filtersSlice.actions
}
